import { useState, useRef } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import IconNB from './IconNB';

interface DatePickerNBProps {
  label?: string;
  value?: string;
  onChange?: (date: string) => void;
  min?: string;
  max?: string;
  error?: string;
  placeholder?: string;
}

const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function toDateInputFormat(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseInputDate(value: string): Date | null {
  if (!value) return null;
  const parts = value.split('-');
  if (parts.length !== 3) return null;
  const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  return isNaN(date.getTime()) ? null : date;
}

function isSameDay(d1: Date, d2: Date): boolean {
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
}

function isDayDisabled(day: number, month: number, year: number, min?: string, max?: string): boolean {
  const date = new Date(year, month, day);
  const minDate = min ? parseInputDate(min) : null;
  const maxDate = max ? parseInputDate(max) : null;

  if (minDate && date < minDate) return true;
  if (maxDate && date > maxDate) return true;
  return false;
}

export default function DatePickerNB({
  label,
  value,
  onChange,
  min,
  max,
  error,
  placeholder = 'Select date',
}: DatePickerNBProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    const d = value ? parseInputDate(value) : null;
    return d || new Date();
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const today = new Date();
  const selectedDate = value ? parseInputDate(value) : null;
  const displayDate = selectedDate || today;

  // Close on outside click
  useClickOutside(containerRef, () => setIsOpen(false), isOpen);

  const handlePrevMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleDayClick = (day: number) => {
    const clickedDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    if (onChange) {
      onChange(toDateInputFormat(clickedDate));
    }
    setIsOpen(false);
  };

  const buildCalendarDays = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Get the day of week for the first day (0 = Sunday, convert to Monday-based)
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6;

    const days: Array<{ day: number | null; disabled: boolean }> = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ day: null, disabled: false });
    }

    // Add days of the month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const disabled = isDayDisabled(d, month, year, min, max);
      days.push({ day: d, disabled });
    }

    return days;
  };

  const days = buildCalendarDays();
  const isToday = (day: number) =>
    day === today.getDate() &&
    viewDate.getMonth() === today.getMonth() &&
    viewDate.getFullYear() === today.getFullYear();

  const isSelected = (day: number) =>
    selectedDate &&
    day === selectedDate.getDate() &&
    viewDate.getMonth() === selectedDate.getMonth() &&
    viewDate.getFullYear() === selectedDate.getFullYear();

  return (
    <div className="flex flex-col gap-1 relative" ref={containerRef}>
      {label && (
        <label className="font-nb font-bold text-sm text-nb-black">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        className={`
          bg-nb-white
          border-3 border-nb-black
          px-4 py-3
          font-nb
          text-left
          w-full
          flex
          justify-between
          items-center
          focus:border-nb-yellow
          focus:outline-none
          ${error ? 'border-nb-red' : ''}
        `.replace(/\s+/g, ' ').trim()}
      >
        <div className="flex items-center gap-2">
          <IconNB name="chevronRight" size={16} className="rotate-[-90deg]" />
          <span className={value ? 'text-nb-black' : 'text-nb-text-muted'}>
            {value ? formatDate(parseInputDate(value)!) : placeholder}
          </span>
        </div>
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 bg-nb-white border-3 border-nb-black shadow-[4px_4px_0px_#000] z-50 p-3 w-72"
          role="listbox"
        >
          {/* Header: Month Year with nav arrows */}
          <div className="flex justify-between items-center mb-3 font-nb font-bold">
            <button
              type="button"
              aria-label="previous month"
              onClick={handlePrevMonth}
              className="w-8 h-8 flex items-center justify-center hover:bg-nb-yellow cursor-pointer border-2 border-nb-black"
            >
              <IconNB name="chevronLeft" size={16} />
            </button>
            <span className="text-sm">
              {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
            </span>
            <button
              type="button"
              aria-label="next month"
              onClick={handleNextMonth}
              className="w-8 h-8 flex items-center justify-center hover:bg-nb-yellow cursor-pointer border-2 border-nb-black"
            >
              <IconNB name="chevronRight" size={16} />
            </button>
          </div>

          {/* Day of week headers */}
          <div className="grid grid-cols-7 gap-1 mb-1 text-xs text-nb-text-muted text-center font-nb">
            {DAYS.map(d => (
              <span key={d} className="text-nb-text-muted">{d}</span>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((item, idx) => {
              if (item.day === null) {
                return <span key={`empty-${idx}`} />;
              }
              const disabled = item.disabled;
              const day = item.day as number;
              const todayClass = isToday(day) ? 'border-nb-black font-bold' : '';
              const selectedClass = isSelected(day) ? 'bg-nb-black text-white' : '';
              const disabledClass = disabled ? 'text-nb-text-muted/30 cursor-not-allowed' : '';

              return (
                <button
                  key={day}
                  type="button"
                  disabled={disabled}
                  onClick={() => !disabled && handleDayClick(day)}
                  className={`
                    w-8 h-8
                    flex
                    items-center
                    justify-center
                    text-sm
                    font-nb
                    border-2
                    border-transparent
                    hover:border-nb-black
                    hover:bg-nb-yellow
                    ${todayClass}
                    ${selectedClass}
                    ${disabledClass}
                  `.replace(/\s+/g, ' ').trim()}
                >
                  {item.day}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {error && (
        <span className="text-xs text-nb-red">{error}</span>
      )}
    </div>
  );
}